import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { Box, ButtonBase, Divider, IconButton, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { type MouseEvent, useMemo } from 'react';

import { useHeaderBlockHeight } from '~/components/global/appBarHeight';
import { ASSISTANT_NAME } from '~/components/otto/branding';
import { useOtto } from '~/components/otto/OttoProvider';
import { isSessionReady, relativeTime } from '~/components/otto/sessions';
import { SURFACE_RADIUS } from '~/theme';

const PANEL_WIDTH = 280;
const DOCK_PANEL_WIDTH = 440;
const DOCK_PANEL_HEIGHT = 800;
const DOCK_OFFSET = 24;
const DOCK_GAP = 12;

export function OttoHistoryPanel() {
  const theme = useTheme();
  const { state, selectSession, deleteSession, newChat, toggleHistory } = useOtto();
  const headerBlockHeight = useHeaderBlockHeight();
  const isDock = state.mode === 'dock';

  const { ready, earlier } = useMemo(() => {
    const ready: string[] = [];
    const earlier: string[] = [];
    for (const id of state.sessionOrder) {
      const s = state.sessions[id];
      if (!s) continue;
      if (isSessionReady(s) && id !== state.activeSessionId) ready.push(id);
      else earlier.push(id);
    }
    return { ready, earlier };
  }, [state.sessionOrder, state.sessions, state.activeSessionId]);

  if (!state.historyOpen) return null;

  const total = state.sessionOrder.length;

  return (
    <Box
      component="aside"
      aria-label={`${ASSISTANT_NAME} history`}
      sx={{
        ...(isDock
          ? {
              position: 'fixed',
              right: DOCK_OFFSET + DOCK_PANEL_WIDTH + DOCK_GAP,
              bottom: DOCK_OFFSET,
              width: PANEL_WIDTH,
              height: DOCK_PANEL_HEIGHT,
              maxHeight: `calc(100vh - ${DOCK_OFFSET * 2}px)`,
              // Matches the docked chat panel it sits beside.
              borderRadius: `${SURFACE_RADIUS}px`,
              border: `1px solid ${alpha('#000', 0.08)}`,
              boxShadow: `0 20px 48px ${alpha('#000', 0.22)}`,
              zIndex: theme.zIndex.modal,
            }
          : {
              position: 'fixed',
              // Whatever OttoPanel is using, so the two sit flush below the bar and level with each
              // other.
              top: `${headerBlockHeight}px`,
              bottom: 0,
              right: 0,
              width: PANEL_WIDTH,
              minWidth: PANEL_WIDTH,
              borderLeft: `1px solid ${alpha('#000', 0.08)}`,
              zIndex: theme.zIndex.drawer,
            }),
        background: alpha('#fff', 0.85),
        backdropFilter: 'blur(12px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              display: 'block',
            }}
          >
            History
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
            {total === 1 ? '1 conversation' : `${total} conversations`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => newChat()} aria-label="New chat">
            <AddRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={() => toggleHistory()} aria-label="Close history">
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
      <Divider sx={{ opacity: 0.5 }} />
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', py: 0.5 }}>
        {total === 0 ? (
          <Typography
            variant="caption"
            sx={{ display: 'block', px: 2, py: 1, color: 'text.disabled' }}
          >
            No previous sessions yet.
          </Typography>
        ) : null}
        {ready.length > 0 ? <SectionHeading label="Ready for you" /> : null}
        {ready.map((id) => (
          <SessionRow
            key={id}
            id={id}
            ready
            active={id === state.activeSessionId}
            onSelect={selectSession}
            onDelete={deleteSession}
          />
        ))}
        {earlier.length > 0 ? <SectionHeading label="Earlier" /> : null}
        {earlier.map((id) => (
          <SessionRow
            key={id}
            id={id}
            ready={false}
            active={id === state.activeSessionId}
            onSelect={selectSession}
            onDelete={deleteSession}
          />
        ))}
      </Box>
    </Box>
  );
}

function SectionHeading({ label }: { label: string }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        px: 2,
        pt: 1.5,
        pb: 0.5,
        fontWeight: 700,
        color: 'text.secondary',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        fontSize: '0.65rem',
      }}
    >
      {label}
    </Typography>
  );
}

function SessionRow({
  id,
  ready,
  active,
  onSelect,
  onDelete,
}: {
  id: string;
  ready: boolean;
  active: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const theme = useTheme();
  const { state } = useOtto();
  const session = state.sessions[id];
  if (!session) return null;
  const stamp = session.lastAssistantAt ?? session.createdAt;

  const handleDelete = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete(id);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        '&:hover .otto-session-delete': { opacity: 1 },
      }}
    >
      <ButtonBase
        onClick={() => onSelect(id)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
          textAlign: 'left',
          px: 2,
          py: 1.25,
          pr: 5,
          background: active ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
          borderLeft: active ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
          '&:hover': { background: alpha(theme.palette.primary.main, 0.04) },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
          {ready ? (
            <Box
              component="span"
              role="status"
              aria-label="Ready"
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: theme.palette.error.main,
                flexShrink: 0,
              }}
            />
          ) : null}
          <Typography
            variant="body2"
            sx={{
              fontWeight: active ? 600 : 400,
              color: active ? theme.palette.primary.dark : 'text.primary',
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: '0.8rem',
            }}
          >
            {session.title}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', fontSize: '0.65rem', mt: 0.25 }}
        >
          {relativeTime(stamp)}
        </Typography>
      </ButtonBase>
      <IconButton
        size="small"
        onClick={handleDelete}
        aria-label={`Delete chat: ${session.title}`}
        className="otto-session-delete"
        sx={{
          position: 'absolute',
          right: 6,
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: 0,
          transition: 'opacity 0.15s',
          color: 'text.secondary',
          '&:hover': {
            color: theme.palette.error.main,
            background: alpha(theme.palette.error.main, 0.08),
          },
          '&:focus-visible': { opacity: 1 },
        }}
      >
        <DeleteRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}
